import { TextWidgetEditor, useGuardEditorState, type VariableMap } from '@features/rich-text-editor';
import { GridDashboardTextItem, InsightDashboardItemType, TextSubType } from '@g17eco/types/insight-custom-dashboard';
import classNames from 'classnames';

const commonClassnames = 'w-100 h-100 d-flex align-items-start text-break overflow-auto';

type Props = {
  item: Pick<GridDashboardTextItem, 'type' | 'subType' | 'text' | 'editorState'>;
  isEditing?: boolean;
  variableMap: VariableMap;
};

export const TextWidget = ({ item, isEditing, variableMap }: Props) => {
  const { type, subType, text, editorState } = item;

  if (subType === TextSubType.Metric) {
    return <TextArea text={text} isEditing={isEditing} />;
  }

  if (editorState) {
    return <RichText editorState={editorState} isEditing={isEditing} variableMap={variableMap} />;
  }

  switch (type) {
    case InsightDashboardItemType.Headline:
      return <Headline text={text} isEditing={isEditing} />;
    case InsightDashboardItemType.Text:
      return <TextArea text={text} isEditing={isEditing} />;
    default:
      return null;
  }
};

const TextArea = ({ text, isEditing }: Pick<Props, 'isEditing'> & Pick<GridDashboardTextItem, 'text'>) => {
  return (
    <div
      className={classNames(
        { 'background-ThemeBgExtralight': isEditing },
        commonClassnames,
        'text-ThemeTextMedium text-area-widget'
      )}
    >
      {text}
    </div>
  );
};

/** @deprecated */
const Headline = ({ text, isEditing }: Pick<Props, 'isEditing'> & Pick<GridDashboardTextItem, 'text'>) => {
  return (
    <h2
      className={classNames(
        { 'background-ThemeBgExtralight': isEditing },
        commonClassnames,
        'text-ThemeHeadingLight px-3 m-0'
      )}
    >
      {text}
    </h2>
  );
};

const RichText = ({
  editorState,
  isEditing,
  variableMap,
}: Pick<Props, 'isEditing' | 'variableMap'> & Pick<GridDashboardTextItem, 'editorState'>) => {
  const { guardEditorState } = useGuardEditorState(editorState);

  // Create a stable key that includes both editorState and variableMap
  // This forces remount when either changes, ensuring variables are replaced correctly
  const editorKey = JSON.stringify(guardEditorState.toJSON());
  const variablesKey = JSON.stringify(variableMap);
  const compositeKey = `${editorKey}-${variablesKey}`;

  return (
    <div className={classNames({ 'background-ThemeBgExtralight': isEditing }, 'w-100 h-100 overflow-auto text-editor-widget')}>
      <TextWidgetEditor
        editorState={guardEditorState}
        variableMap={variableMap}
        key={compositeKey}
      />
    </div>
  );
};
